import React from 'react';
import { Box, Typography, Card, Chip, Switch } from '@mui/joy';
import { Draggable } from '@hello-pangea/dnd';

import { supabase } from '../../supabaseClient';

const TaskCard = ({ task, index, onClick, canManageTasks }) => {
  // Complete toggle handler
  const handleCompleteToggle = async (event) => {
    event.stopPropagation();
    if (task.completed) return;
    await supabase
      .from('tasks')
      .update({ completed: true })
      .eq('id', task.id);
    // Optionally, you can call a prop like onTaskUpdated() to refresh tasks
    // For now, rely on real-time updates or parent refresh
  };
  // Priority colors, icons, and drop shadows
  const getPriorityConfig = (priority) => {
    switch (Number(priority)) {
      case 1:
        return { 
          color: 'danger', 
          icon: 'priority_high',
          label: 'High (1)',
          dropShadow: '0 4px 12px rgba(220, 53, 69, 0.3)' // Red shadow
        };
      case 2:
        return { 
          color: 'warning', 
          icon: 'drag_handle',
          label: 'Medium (2)',
          dropShadow: '0 4px 12px rgba(255, 20, 147, 0.3)' // Pink shadow
        };
      case 3:
        return { 
          color: 'success', 
          icon: 'low_priority',
          label: 'Low (3)',
          dropShadow: '0 4px 12px rgba(255, 165, 0, 0.3)' // Orange shadow
        };
      default:
        return { 
          color: 'neutral', 
          icon: 'drag_handle',
          label: 'No Priority',
          dropShadow: '0 4px 12px rgba(40, 167, 69, 0.3)' // Green shadow for null priority
        };
    }
  };

  const priorityConfig = getPriorityConfig(task.priority);

  // Check if task is overdue
  const isOverdue = task.due_date && new Date(task.due_date) < new Date();

  // Format due date
  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    
    // Check if it's today
    const isToday = date.toDateString() === now.toDateString();
    
    // Check if it's tomorrow
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    if (isOverdue) return `Due ${date.toLocaleDateString()}`;
    
    return `Due ${date.toLocaleDateString()}`;
  };

  const dueText = formatDueDate(task.due_date);

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onClick}
          sx={{
            p: 2,
            mb: 1,
            cursor: 'pointer',
            backgroundColor: 'background.body',
            border: '1px solid',
            borderColor: snapshot.isDragging ? 'success.400' : 'neutral.200',
            boxShadow: snapshot.isDragging 
              ? '0 8px 25px rgba(0, 168, 132, 0.25)' 
              : priorityConfig.dropShadow, // Use priority-based drop shadow
            transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'success.300',
              transform: 'translateY(-2px)',
              boxShadow: priorityConfig.dropShadow, // Maintain priority shadow on hover
            },
            // Left border color based on priority
            borderLeft: `4px solid`,
            borderLeftColor: `${priorityConfig.color}.500`,
          }}
        >
          {/* Task Title */}
          <Typography 
            level="title-sm" 
            sx={{ 
              mb: 1,
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {task.title}
          </Typography>

          {/* Task Description */}
          {task.description && (
            <Typography 
              level="body-xs" 
              sx={{ 
                mb: 2,
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {task.description}
            </Typography>
          )}

          {/* Task Meta Information */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Due Date */}
            {dueText && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span 
                  className="material-symbols-outlined" 
                  style={{ 
                    fontSize: '14px', 
                    color: isOverdue ? 'var(--joy-palette-danger-500)' : 'var(--joy-palette-neutral-500)' 
                  }}
                >
                  schedule
                </span>
                <Typography 
                  level="body-xs" 
                  sx={{ 
                    color: isOverdue ? 'danger.600' : 'neutral.600',
                    fontWeight: isOverdue ? 600 : 400,
                  }}
                >
                  {dueText}
                </Typography>
              </Box>
            )}

            {/* Bottom Row - Priority and Indicators */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Priority Chip */}
              <Chip
                size="sm"
                variant="soft"
                color={priorityConfig.color}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '12px' }}>
                    {priorityConfig.icon}
                  </span>
                }
                sx={{ fontSize: '0.7rem' }}
              >
                {priorityConfig.label}
              </Chip>

              {/* Complete Toggle */}
              {canManageTasks && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <Typography level="body-xs" sx={{ mr: 0.5 }}>
                    Mark Complete
                  </Typography>
                  <Switch
                    checked={!!task.completed}
                    onChange={handleCompleteToggle}
                    color="success"
                    size="sm"
                    disabled={!!task.completed}
                    slotProps={{ thumb: { style: { backgroundColor: task.completed ? '#4caf50' : undefined } } }}
                  >
                    {task.completed ? 'Completed' : 'Complete'}
                  </Switch>
                </Box>
              )}

              {/* Task Indicators */}
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {/* Comments Count */}
                {task.task_comments && task.task_comments.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <span 
                      className="material-symbols-outlined" 
                      style={{ fontSize: '14px', color: 'var(--joy-palette-neutral-500)' }}
                    >
                      chat_bubble
                    </span>
                    <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                      {task.task_comments.length}
                    </Typography>
                  </Box>
                )}

                {/* Attachments Count */}
                {task.task_attachments && task.task_attachments.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <span 
                      className="material-symbols-outlined" 
                      style={{ fontSize: '14px', color: 'var(--joy-palette-neutral-500)' }}
                    >
                      attach_file
                    </span>
                    <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                      {task.task_attachments.length}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Card>
      )}
    </Draggable>
  );
};

export default TaskCard;